import type { PublisherFilterState, PublisherManageDto } from '../../../types/managePublishers';
import { formatDrmQuotaSummary } from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  rows: PublisherManageDto[];
  totalCount: number;
  filters: PublisherFilterState;
  onChange: (patch: Partial<PublisherFilterState>) => void;
  selection: Map<string, PublisherManageDto>;
  onToggleRow: (row: PublisherManageDto) => void;
  onTogglePage: (rows: PublisherManageDto[], all: boolean) => void;
  onEdit: (id: string) => void;
}

export default function PublisherListTable({
  rows, totalCount, filters, onChange, selection, onToggleRow, onTogglePage, onEdit,
}: Props) {
  const { t } = useTranslation();
  const allOnPageSelected = rows.length > 0 && rows.every(r => selection.has(r.id));
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));

  const sortable = (field: 'name' | 'creationTime' | 'bookCount', label: string) => {
    const active = filters.sort === field;
    const nextDir: 'asc' | 'desc' = active && filters.dir === 'asc' ? 'desc' : 'asc';
    return (
      <button
        type="button"
        onClick={() => onChange({ sort: field, dir: nextDir, page: 1 })}
        className={`text-xs uppercase tracking-wide ${active ? 'text-primary-600' : 'text-neutral-600'}`}
      >
        {label} {active ? (filters.dir === 'asc' ? '▲' : '▼') : ''}
      </button>
    );
  };

  return (
    <div>
      <div className="overflow-x-auto border border-neutral-200 rounded">
      <table className="min-w-full text-sm">
        <thead className="bg-neutral-50">
          <tr>
            <th className="p-2 w-8">
              <input
                type="checkbox"
                checked={allOnPageSelected}
                onChange={(e) => onTogglePage(rows, e.target.checked)}
                aria-label={t('App.Admin.Publishers.PublisherListTable.SelectAllAriaLabel')}
              />
            </th>
            <th className="p-2 text-left">{sortable('name', t('App.Admin.Publishers.PublisherListTable.ColName'))}</th>
            <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Publishers.PublisherListTable.ColContactEmail')}</th>
            <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Publishers.PublisherListTable.ColWebsite')}</th>
            <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Publishers.PublisherListTable.ColActive')}</th>
            <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Publishers.PublisherListTable.ColDRM')}</th>
            <th className="p-2 text-left text-xs uppercase tracking-wide text-neutral-600">{t('App.Admin.Publishers.PublisherListTable.ColDRMQuotas')}</th>
            <th className="p-2 text-right">{sortable('bookCount', t('App.Admin.Publishers.PublisherListTable.ColBooks'))}</th>
            <th className="p-2 text-left">{sortable('creationTime', t('App.Admin.Publishers.PublisherListTable.ColCreated'))}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.id}
              className={`border-t border-neutral-100 ${r.isDeleted ? 'opacity-50 bg-neutral-50' : 'hover:bg-neutral-50'}`}
            >
              <td className="p-2">
                <input
                  type="checkbox"
                  checked={selection.has(r.id)}
                  onChange={() => onToggleRow(r)}
                  aria-label={`Select ${r.name}`}
                />
              </td>
              <td className="p-2">
                <button type="button" onClick={() => onEdit(r.id)} className="text-primary-600 hover:underline">
                  {r.name}
                </button>
                {r.isDeleted && <span className="ml-2 text-xs uppercase text-red-600">{t('App.Admin.Publishers.PublisherListTable.DeletedBadge')}</span>}
              </td>
              <td className="p-2">{r.contactEmail ?? <span className="text-neutral-400">—</span>}</td>
              <td className="p-2">
                {r.website
                  ? <a href={r.website} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">{r.website}</a>
                  : <span className="text-neutral-400">—</span>}
              </td>
              <td className="p-2">{r.isActive ? t('App.Common.Yes') : t('App.Common.No')}</td>
              <td className="p-2">{r.isDrmEnabled ? t('App.Common.On') : t('App.Common.Off')}</td>
              <td className="p-2 text-xs whitespace-nowrap">{formatDrmQuotaSummary(r)}</td>
              <td className="p-2 text-right tabular-nums">{r.bookCount}</td>
              <td className="p-2">{new Date(r.creationTime).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="p-6 text-center text-sm text-neutral-500">{t('App.Admin.Publishers.PublisherListTable.NoResults')}</div>
      )}
      </div>

      <div className="flex items-center justify-between mt-3 text-sm">
        <div className="flex items-center gap-3 text-neutral-600">
          <span>
            {t('App.Admin.Publishers.PublisherListTable.PageSummary', { count: totalCount, page: filters.page, totalPages })}
          </span>
          <label className="flex items-center gap-1">
            <span className="text-xs">{t('App.Admin.Publishers.PublisherListTable.RowsLabel')}</span>
            <select
              value={filters.pageSize}
              onChange={(e) => onChange({ pageSize: Number(e.target.value), page: 1 })}
              className="border border-neutral-300 rounded px-1 py-0.5 text-sm"
              aria-label={t('App.Admin.Publishers.PublisherListTable.RowsAriaLabel')}
            >
              {[10, 25, 50, 100].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={filters.page <= 1}
            onClick={() => onChange({ page: filters.page - 1 })}
            className="px-3 py-1 border rounded disabled:opacity-50 min-h-[44px]"
          >
            {t('App.Admin.Publishers.PublisherListTable.PrevButton')}
          </button>
          <button
            type="button"
            disabled={filters.page >= totalPages}
            onClick={() => onChange({ page: filters.page + 1 })}
            className="px-3 py-1 border rounded disabled:opacity-50 min-h-[44px]"
          >
            {t('App.Admin.Publishers.PublisherListTable.NextButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
